import { CalendarDays } from 'lucide-react';

import { Button, EmptyState } from '@/shared';
import { t } from '@/i18n';
import type { I18nKey } from '@/i18n';

// TRAD-2831 — EventList 안에 비공개로 있던 것을 «그대로» 꺼냈다(리스트·주간·월간이 같은 0건 빈 상태를 말하게).
// 판정 입력(hasFilter · expandableTotal)은 model/utils 의 `hasActiveCalendarFilter` · `resolveExpandableTotal` 로 만든다.
// TRAD-2106 — 로컬 EmptyState 사본 제거. 공용 `shared/ui/EmptyState` 로 이관하며 셋을 고쳤다:
//   ① `📅` **이모지 아이콘 → lucide `CalendarDays`** (감사 SoT-14 "이모지를 아이콘으로 쓰지 않는다")
//   ② 하드코딩 한글 3개 → 카탈로그 키(`financial_calendar.empty.*`) — EN 로케일 한글 노출 차단
//   ③ "필터 초기화" 버튼은 공용 컴포넌트의 `action` 으로 전달
// 🔴 이름을 `EmptyState` 로 두지 않는다 — TRAD-2107(2-b) 에서 승격된 공용 컴포넌트와 이름이
// 겹쳐 로컬 래퍼가 자기 자신을 재귀 호출하는 사고가 실제로 났다. 그래서 `CalendarEmptyState`.
// 🔴 `action` 은 "필터 초기화"뿐이다 — **재시도 버튼이 아니다.** 0건은 다시 불러도 0건이고,
// 여기에 재시도를 붙이면 빈 상태가 ErrorState 와 구분되지 않는다.
// TRAD-2334 — 0건이지만 **중요도 필터만 풀면 볼 게 있는** 상태를 따로 말해 준다.
//
// 실측 계기(2026-08-05, dev 2026년 전체): 국가 칩 `기타`만 켜면 `country=OTHER` 는 235건인데
// `OTHER + importance=HIGH` 는 **0건**이다 — 기타 이벤트에 HIGH 가 하나도 없기 때문이다.
// 기본 노출 정책이 HIGH만이라(TRAD-2071) 사용자에겐 "칩이 고장난 것"으로 보인다.
//
// 🔴 화면이 아는 사실만 말한다 — 없는 문구를 지어내지 않는다. 건수(`m`)는 daily-counts 가
// 실제로 내려준 값이고, 백엔드 미연동이면 `undefined` 라 이 분기 자체가 서지 않는다.
// 재시도 버튼이 아니라 **필터를 넓히는 행동**을 준다: 0건은 다시 불러도 0건이다(TRAD-2107 원칙).
export const CalendarEmptyState = ({
  hasFilter,
  onReset,
  expandableTotal,
  onExpandToAll,
  periodTitleKey = 'financial_calendar.empty.period',
}: {
  hasFilter: boolean;
  /** TRAD-3725 — 필터 없이 0건일 때 문구 키. 일자 페이지는 «기간»이 아니라 «그날»이라 `financial_calendar.empty.day`. */
  periodTitleKey?: I18nKey;
  onReset: () => void;
  /** 중요도 필터만 풀었을 때 보이는 건수. `undefined` = 그런 상태가 아니거나 집계를 모른다. */
  expandableTotal?: number;
  onExpandToAll?: () => void;
}) => {
  const canExpand = expandableTotal !== undefined && onExpandToAll !== undefined;

  return (
    <EmptyState
      size="md"
      icon={CalendarDays}
      // `title`(이미 t() 를 거친 문자열) 경로를 쓴다 — 건수 보간이 필요한데 `titleKey` 는 보간을 받지 않는다.
      title={canExpand ? t('financial_calendar.empty.impact_filtered') : undefined}
      titleKey={
        canExpand ? undefined : hasFilter ? 'financial_calendar.empty.filtered' : periodTitleKey
      }
      action={
        canExpand ? (
          // 날짜 헤더의 "전체 M건 보기"와 **같은 문구·같은 동작**(onExpandToAll)을 쓴다.
          <Button variant="outline" size="sm" onClick={onExpandToAll}>
            {t('financial_calendar.date_header.view_all_count', { m: String(expandableTotal) })}
          </Button>
        ) : hasFilter ? (
          <Button variant="outline" size="sm" onClick={onReset}>
            {t('financial_calendar.empty.reset_filter')}
          </Button>
        ) : undefined
      }
    />
  );
};
